import { CloseIcon, EditIcon } from '@chakra-ui/icons';
import { Alert, AlertDescription, AlertIcon, Box, Button, Center, FormControl, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Table, Tbody, Td, Text, Textarea, Th, Thead, Tr, useDisclosure, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { alert, hasPermission, useTableColors } from '../../Utils';
import { createRole, deleteRole, getAllRoles } from '../../api';
import AccessDenied from '../../components/AccessDenied';
import Loading from '../../components/Loading/Loading';
import PopoverId from '../../components/PopoverId';
import TitleHeader from '../../components/TitleHeader';

const RolesManagement = () => {
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [newRole, setNewRole] = useState({ name: '', description: '', permissions: [] });
    const [errorModal, setErrorModal] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: closeCreateModal } = useDisclosure();
    const navigate = useNavigate();
    const coolors = useTableColors();

    const onCreateClose = () => {
        setErrorModal(null);
        closeCreateModal();
    };

    useEffect(() => {
        fetchRoles();
    }, []);

    const fetchRoles = async () => {
        try {
            const result = await getAllRoles();
            if (result.success) setRoles(result.data);
            else setError(result.error);
        } catch (error) {
            setError(error);
        }
        setLoading(false);
    };

    const handleSubmitCreate = async () => {
        setErrorModal(null);
        try {
            if (!newRole.name || !newRole.description) {
                setErrorModal("Name and description are required.");
                return;
            }
            newRole.permissions = ['updateAccount'];
            const createdRole = await createRole({ ...newRole });
            if (createdRole.success) {
                alert('Role Created', "success", "Role created successfully", "Okay !");
                fetchRoles();
                onCreateClose();
            } else {
                setErrorModal(createdRole.error);
            }
        } catch (error) {
            console.error('Error creating role:', error);
        }
    };

    const handleSubmitDelete = async () => {
        try {
            await deleteRole(selectedRole._id);
            fetchRoles();
            onDeleteClose();
        } catch (error) {
            console.error('Error deleting role:', error);
        }
    };

    if (loading) {
        return (
            <Center mt={"20%"}>
                <Loading />
            </Center>
        );
    }

    return (
        <Box maxW="full" mx="auto">
            <VStack spacing={6} align="stretch">
                {!error ?
                    <>
                        <TitleHeader title={"Roles Management"} buttonText={hasPermission('createRole') ? "New Role" : null} onClick={hasPermission('createRole') ? () => {
                            setNewRole({ name: '', description: '', permissions: '' });
                            onCreateOpen();
                        } : null} />

                        <Table size="sm" variant="simple">
                            <Thead bg={coolors.theadBg} color={coolors.textColor}>
                                <Tr>
                                    <Th>#</Th>
                                    <Th>Name</Th>
                                    <Th>Description</Th>
                                    <Th>Actions</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {roles.map((role, index) => (
                                    <Tr
                                        key={role._id}
                                        borderBottom={`2px solid ${coolors.borderColor}`}
                                        bg={index % 2 === 0 ? coolors.rowBgEven : coolors.rowBgOdd}
                                        color={coolors.textColor}
                                        _hover={{ bg: coolors.rowHoverBg, color: "white" }}
                                    >
                                        <Td><PopoverId place="Roles Management" index={index} content={role._id} /></Td>
                                        <Td>{role.name}</Td>
                                        <Td>{role.description}</Td>
                                        <Td>
                                            <HStack spacing={4}>
                                                {hasPermission("updateRole") && (
                                                    <Button size="xs" onClick={() => navigate(`/roles/${role._id}`)} colorScheme="green">
                                                        <EditIcon mr={2} /> Edit
                                                    </Button>
                                                )}
                                                {hasPermission("deleteRole") && (
                                                    <Button
                                                        size="xs"
                                                        onClick={() => {
                                                            setSelectedRole(role);
                                                            onDeleteOpen();
                                                        }}
                                                        colorScheme="red"
                                                    >
                                                        <CloseIcon mr={2} /> Delete
                                                    </Button>
                                                )}
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>


                        {hasPermission('createRole') &&
                            <Modal isOpen={isCreateOpen} scrollBehavior={'inside'} size={"2xl"} onClose={onCreateClose}>
                                <ModalOverlay />
                                <ModalContent>
                                    <ModalHeader>Create Role</ModalHeader>
                                    <ModalCloseButton />
                                    {errorModal &&
                                        <Alert status='error'>
                                            <AlertIcon />
                                            <AlertDescription>{errorModal}</AlertDescription>
                                        </Alert>
                                    }
                                    <ModalBody>
                                        <FormControl mt={2}>
                                            <Text>Name</Text>
                                            <Input value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} />
                                        </FormControl>
                                        <FormControl mt={5}>
                                            <Text>Description</Text>
                                            <Textarea value={newRole.description} onChange={(e) => setNewRole({ ...newRole, description: e.target.value })} />
                                        </FormControl>
                                    </ModalBody>
                                    <ModalFooter>
                                        <Button colorScheme="blue" onClick={handleSubmitCreate}>Create</Button>
                                        <Button ml={3} onClick={onCreateClose}>Cancel</Button>
                                    </ModalFooter>
                                </ModalContent>
                            </Modal>
                        }

                        {hasPermission('deleteRole') &&
                            <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
                                <ModalOverlay />
                                <ModalContent>
                                    <ModalHeader>Delete Role</ModalHeader>
                                    <ModalCloseButton />
                                    <ModalBody>
                                        Are you sure you want to delete the role "{selectedRole?.name}"?
                                    </ModalBody>
                                    <ModalFooter>
                                        <Button colorScheme="red" onClick={handleSubmitDelete}>Yes, Delete</Button>
                                        <Button ml={3} onClick={onDeleteClose}>No</Button>
                                    </ModalFooter>
                                </ModalContent>
                            </Modal>
                        }
                    </>
                    :
                    <AccessDenied msg={error} />
                }
            </VStack>
        </Box>
    );
};

export default RolesManagement;
